/**
 * Generate contextual risk warnings based on the current configuration.
 * Each warning has: id, severity ('info' | 'warning' | 'danger'), textKey (i18n key)
 */
export function generateWarnings(costs) {
  const warnings = [];

  const entityType = costs.taxEntity?.type;
  const rentalType = costs.revenue?.rentalType;
  const horizonYears = costs.projection?.horizonYears || 15;
  const yearBuilt = costs.property?.yearBuilt || 1985;

  // Gewerblicher Grundstückshandel (3-Objekte-Grenze)
  // Private entities selling within 10 years risk commercial property dealer classification
  if (['private', 'gmbh_co_kg'].includes(entityType) && horizonYears < 10) {
    warnings.push({
      id: 'gewerblich',
      severity: 'warning',
      textKey: 'warning.gewerblich',
    });
  }

  // Immo-GmbH + short-term rental = loss of erweiterte Kürzung
  if (entityType === 'immo_gmbh' && rentalType === 'shortterm') {
    warnings.push({
      id: 'immo_gmbh_shortterm',
      severity: 'danger',
      textKey: 'warning.immoGmbhShortterm',
    });
  }

  // MoPeG 2024 for GmbH & Co. KG
  if (entityType === 'gmbh_co_kg') {
    warnings.push({
      id: 'mopeg',
      severity: 'info',
      textKey: 'warning.mopeg',
    });
  }

  // GEG heating obligation for old buildings
  if (yearBuilt < 1994) {
    warnings.push({
      id: 'geg_heating',
      severity: 'info',
      textKey: 'warning.geg_heating',
    });
  }

  // Short-term rental with hotel services + private entity = GewSt risk
  if (rentalType === 'shortterm' && costs.vat?.hotelServices && entityType === 'private') {
    warnings.push({
      id: 'gewst_shortterm',
      severity: 'warning',
      textKey: 'warning.gewstShortterm',
    });
  }

  // §15a: KG loss may exceed Kommanditeinlage
  if (entityType === 'gmbh_co_kg') {
    const kommanditeinlage = costs.taxEntity?.kommanditeinlage || 25000;
    const debtCapital = costs.purchase?.debtCapital || 0;
    // High leverage with KG = likely §15a losses
    if (debtCapital > kommanditeinlage * 3) {
      warnings.push({
        id: 'section15a',
        severity: 'info',
        textKey: 'warning.section15a',
      });
    }
  }

  // High LTV (> 80%)
  const purchasePrice = costs.property?.purchasePrice || 0;
  const debtCapital = costs.purchase?.debtCapital || 0;
  if (purchasePrice > 0 && debtCapital / purchasePrice > 0.8) {
    warnings.push({
      id: 'high_ltv',
      severity: 'warning',
      textKey: 'warning.highLTV',
    });
  }

  // Low DSCR (< 1.25)
  const annualRent = (costs.revenue?.kaltmietePerSqm || 0) * (costs.property?.squareMeters || 0) * 12;
  const interestRate = (costs.loan?.interestRate || 0) / 100;
  const tilgung = (costs.loan?.tilgungPercent || 0) / 100;
  const annualDebtService = debtCapital * (interestRate + tilgung);
  if (annualDebtService > 0 && annualRent > 0 && annualRent / annualDebtService < 1.25) {
    warnings.push({
      id: 'low_dscr',
      severity: 'warning',
      textKey: 'warning.lowDSCR',
    });
  }

  // Abgeltungsteuer info for corporate entities
  if (['gmbh', 'immo_gmbh', 'stiftung'].includes(entityType)) {
    warnings.push({
      id: 'abgeltungsteuer',
      severity: 'info',
      textKey: 'warning.abgeltungsteuer',
    });
  }

  return warnings;
}
